import React, { useState, useEffect } from "react";
import { ref, onValue, remove, push, update } from "firebase/database";
import { db } from "./firebase";
import { formatCourseId } from "./utils/formatCourse";
import DriveBulkImport from "./components/DriveBulkImport";

const AdminVideos = () => {
  const [videos, setVideos] = useState([]);
  const [courses, setCourses] = useState([]);
  const [activeCourse, setActiveCourse] = useState("OL");
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({
    title: "",
    url: "",
    published: true,
    chapter: "Uncategorized" // Default chapter
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddVideoExpanded, setIsAddVideoExpanded] = useState(false);
  const [expandedVideoId, setExpandedVideoId] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc"
  });
  const [chapters, setChapters] = useState(["Uncategorized"]);
  const [isAddingChapter, setIsAddingChapter] = useState(false);
  const [newChapterName, setNewChapterName] = useState("");
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [collapsedChapters, setCollapsedChapters] = useState({});

  // Fetch courses from Firebase
  useEffect(() => {
    const coursesRef = ref(db, "courses");
    const unsubscribeCourses = onValue(coursesRef, (snapshot) => {
      if (snapshot.exists()) {
        const coursesData = Object.keys(snapshot.val());
        setCourses(coursesData);
        if (!activeCourse && coursesData.length > 0) {
          setActiveCourse(coursesData[0]);
        }
      }
    });

    return () => unsubscribeCourses();
  }, [activeCourse]);

  // Fetch videos and chapters for the active course from Firebase
  useEffect(() => {
    if (!activeCourse) return;

    const videosRef = ref(db, `courses/${activeCourse}/videos`);
    const unsubscribeVideos = onValue(videosRef, (snapshot) => {
      if (snapshot.exists()) {
        const videosData = Object.entries(snapshot.val()).map(([id, data]) => ({
          id,
          ...data,
          published: data.published !== false,
          chapter: data.chapter || "Uncategorized",
          title: data.title || "",
          url: data.url || ""
        }));
        setVideos(videosData);

        // Extract unique chapters
        const uniqueChapters = [...new Set(videosData.map(video => video.chapter || "Uncategorized"))];
        setChapters(uniqueChapters);
      } else {
        setVideos([]);
        setChapters(["Uncategorized"]);
      }
    });

    return () => unsubscribeVideos();
  }, [activeCourse]);

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const startEditing = (video) => {
    setEditingId(video.id);
    setEditData({
      title: video.title || "",
      url: video.url || "",
      published: video.published !== false,
      chapter: video.chapter || "Uncategorized"
    });
    setExpandedVideoId(null);
  };

  const saveEdit = () => {
    if (!editingId || !editData.title || !editData.url) {
      alert("Title and URL are required");
      return;
    }

    // Ensure chapter is set and not empty
    const videoData = {
      ...editData,
      published: editData.published !== false,
      chapter: editData.chapter || "Uncategorized",
      title: editData.title.trim(),
      url: editData.url.trim()
    };

    update(ref(db, `courses/${activeCourse}/videos/${editingId}`), videoData)
      .then(() => {
        alert("Video updated successfully");
        setEditingId(null);
      })
      .catch(error => alert("Error updating video: " + error.message));
  };

  const addNewVideo = () => {
    if (!editData.title || !editData.url) {
      alert("Title and URL are required");
      return;
    }

    // Ensure chapter is set and not empty
    const videoData = {
      ...editData,
      published: editData.published !== false,
      chapter: editData.chapter || "Uncategorized",
      title: editData.title.trim(),
      url: editData.url.trim()
    };

    push(ref(db, `courses/${activeCourse}/videos`), videoData)
      .then(() => {
        alert("Video added successfully");
        setIsAddVideoExpanded(false);
        // Reset form with the same chapter selected
        setEditData({
          title: "",
          url: "",
          published: true,
          chapter: videoData.chapter // Keep the same chapter selected
        });
      })
      .catch(error => alert("Error adding video: " + error.message));
  };

  const addNewChapter = () => {
    if (!newChapterName.trim()) {
      alert("Chapter name is required");
      return;
    }

    if (chapters.includes(newChapterName)) {
      alert("Chapter already exists");
      return;
    }

    setChapters([...chapters, newChapterName]);
    setNewChapterName("");
    setIsAddingChapter(false);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this video?")) {
      remove(ref(db, `courses/${activeCourse}/videos/${id}`))
        .catch(error => alert("Error deleting video: " + error.message));
    }
  };

  const toggleVideoPreview = (id) => {
    setExpandedVideoId(expandedVideoId === id ? null : id);
  };

  const togglePublishStatus = async (id, currentStatus) => {
    try {
      await update(ref(db, `courses/${activeCourse}/videos/${id}`), {
        published: !currentStatus
      });
    } catch (error) {
      alert("Error updating publish status: " + error.message);
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedVideos = React.useMemo(() => {
    let sortableVideos = [...videos];
    if (sortConfig.key) {
      sortableVideos.sort((a, b) => {
        if (sortConfig.key === 'published') {
          const aValue = a.published ? 1 : 0;
          const bValue = b.published ? 1 : 0;
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        const aValue = a[sortConfig.key] || "";
        const bValue = b[sortConfig.key] || "";
        if (sortConfig.direction === "asc") {
          return aValue.toString().localeCompare(bValue.toString());
        } else {
          return bValue.toString().localeCompare(aValue.toString());
        }
      });
    }
    return sortableVideos;
  }, [videos, sortConfig]);

  const filteredVideos = sortedVideos.filter(video =>
    (video.title?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (video.url?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  );

  // Group videos by chapter
  const videosByChapter = filteredVideos.reduce((acc, video) => {
    const chapter = video.chapter || "Uncategorized";
    if (!acc[chapter]) {
      acc[chapter] = [];
    }
    acc[chapter].push(video);
    return acc;
  }, {});

  // Toggle chapter collapse
  const toggleChapter = (chapter) => {
    setCollapsedChapters(prev => ({
      ...prev,
      [chapter]: !prev[chapter]
    }));
  };

  // Function to convert video URL to embed URL
  const getEmbedUrl = (url) => {
    if (!url) return null;

    // Handle YouTube URLs
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      let videoId = null;
      if (url.includes('youtube.com/watch?v=')) {
        videoId = url.split('v=')[1]?.split('&')[0];
      } else if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1]?.split('?')[0];
      } else if (url.includes('youtube.com/embed/')) {
        videoId = url.split('embed/')[1]?.split('?')[0];
      }
      return videoId ? `https://www.youtube.com/embed/${videoId}?rel=0` : null;
    }

    // Handle Vimeo URLs
    if (url.includes('vimeo.com')) {
      let videoId = null;

      // Handle regular vimeo.com URLs
      if (url.includes('vimeo.com/')) {
        const match = url.match(/vimeo\.com\/(\d+)/);
        if (match) {
          videoId = match[1];
        }
      }

      // Handle player.vimeo.com URLs
      if (url.includes('player.vimeo.com')) {
        const match = url.match(/player\.vimeo\.com\/video\/(\d+)/);
        if (match) {
          videoId = match[1];
        }
      }

      // Handle vimeo.com/embed/ URLs
      if (url.includes('vimeo.com/embed/')) {
        const match = url.match(/vimeo\.com\/embed\/(\d+)/);
        if (match) {
          videoId = match[1];
        }
      }

      return videoId ? `https://player.vimeo.com/video/${videoId}?autoplay=0&title=0&byline=0&portrait=0` : null;
    }

    // Handle Google Drive URLs
    if (url.includes('drive.google.com')) {
      if (url.includes('/file/d/')) {
        const fileId = url.split('/file/d/')[1]?.split('/')[0];
        return fileId ? `https://drive.google.com/file/d/${fileId}/preview` : null;
      } else if (url.includes('id=')) {
        const fileId = url.split('id=')[1]?.split('&')[0];
        return fileId ? `https://drive.google.com/file/d/${fileId}/preview` : null;
      }
    }

    // Handle Zoom Recording URLs
    if (url.includes('zoom.us/rec')) {
      // Handle us02web.zoom.us format
      if (url.includes('us02web.zoom.us')) {
        const sharePart = url.split('/rec/share/')[1];
        if (sharePart) {
          const [recordingId, token] = sharePart.split('.');
          if (recordingId && token) {
            return `https://us02web.zoom.us/rec/play/${recordingId}?token=${token}&embedded=true&autoplay=0&mode=play`;
          }
        }
      }
      // Handle regular zoom.us format
      else if (url.includes('/share/')) {
        const sharePart = url.split('/share/')[1];
        if (sharePart) {
          const [recordingId, token] = sharePart.split('?');
          if (recordingId && token) {
            return `https://zoom.us/recording/play/${recordingId}?token=${token}&embedded=true&autoplay=0&mode=play`;
          }
        }
      } else if (url.includes('/play/')) {
        const baseUrl = url.split('?')[0];
        const params = new URLSearchParams(url.split('?')[1] || '');
        params.set('embedded', 'true');
        params.set('autoplay', '0');
        params.set('mode', 'play');
        return `${baseUrl}?${params.toString()}`;
      }
    }

    return null;
  };

  return (
    <div className="p-3 sm:p-4 md:p-6 text-gray-900 min-h-screen">
      <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-gray-900">Videos</h1>

      {/* Course Tabs */}
      <div className="flex flex-wrap gap-2 mb-4 sm:mb-6 p-2 overflow-x-auto">
        {courses.map(course => (
          <button
            key={course}
            onClick={() => {
              setActiveCourse(course);
              setExpandedVideoId(null);
            }}
            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-md transition-colors text-sm sm:text-base whitespace-nowrap ${activeCourse === course
              ? "bg-teal-600 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
          >
            {formatCourseId(course)}
          </button>
        ))}
      </div>

      {/* Search and Add Video */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <input
          type="text"
          placeholder="Search videos..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="p-2 sm:p-2.5 border rounded-md text-gray-900 text-sm sm:text-base"
        />
        <div className="md:col-span-2 flex flex-wrap justify-end gap-2">
          <button
            onClick={() => setIsAddingChapter(!isAddingChapter)}
            className="bg-green-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-md hover:bg-green-700 text-sm sm:text-base whitespace-nowrap"
          >
            {isAddingChapter ? "Cancel" : "Add Chapter"}
          </button>
          <button
            onClick={() => setShowBulkImport(true)}
            className="bg-purple-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-md hover:bg-purple-700 text-sm sm:text-base whitespace-nowrap"
          >
            Bulk Import
          </button>
          <button
            onClick={() => setIsAddVideoExpanded(!isAddVideoExpanded)}
            className="bg-teal-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-md hover:bg-teal-700 text-sm sm:text-base whitespace-nowrap"
          >
            {isAddVideoExpanded ? "Cancel" : "Add Video"}
          </button>
        </div>
      </div>

      {/* Add Chapter Form */}
      {isAddingChapter && (
        <div className="bg-white p-4 rounded-lg shadow-md mb-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900">Add New Chapter</h2>
          <div className="flex space-x-2">
            <input
              type="text"
              value={newChapterName}
              onChange={(e) => setNewChapterName(e.target.value)}
              placeholder="Enter chapter name"
              className="flex-1 p-2 border rounded-md text-gray-900"
            />
            <button
              onClick={addNewChapter}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
            >
              Add Chapter
            </button>
          </div>
        </div>
      )}

      {/* Add Video Form */}
      {isAddVideoExpanded && (
        <div className="bg-white p-4 rounded-lg shadow-md mb-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900">Add New Video</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title*</label>
              <input
                type="text"
                name="title"
                value={editData.title}
                onChange={handleEditChange}
                className="w-full p-2 border rounded-md text-gray-900"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Video URL*</label>
              <input
                type="url"
                name="url"
                value={editData.url}
                onChange={handleEditChange}
                className="w-full p-2 border rounded-md text-gray-900"
                required
                placeholder="YouTube or Google Drive URL"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Chapter</label>
              <select
                name="chapter"
                value={editData.chapter}
                onChange={handleEditChange}
                className="w-full p-2 border rounded-md text-gray-900"
              >
                {chapters.map(chapter => (
                  <option key={chapter} value={chapter}>{chapter}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="publish-new"
                name="published"
                checked={editData.published}
                onChange={handleEditChange}
                className="h-5 w-5 text-teal-600"
              />
              <label htmlFor="publish-new" className="ml-2 text-sm text-gray-700">
                Publish (visible to students)
              </label>
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-2">
            <button
              onClick={() => setIsAddVideoExpanded(false)}
              className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={addNewVideo}
              className="bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700"
            >
              Add Video
            </button>
          </div>
        </div>
      )}

      {/* Videos by Chapter */}
      {Object.entries(videosByChapter).map(([chapter, chapterVideos]) => (
        <div key={chapter} className="mb-6 sm:mb-8">
          <div 
            className="flex items-center justify-between bg-teal-50 hover:bg-teal-100 p-3 sm:p-4 rounded-t-lg cursor-pointer transition-colors border-b border-teal-200"
            onClick={() => toggleChapter(chapter)}
          >
            <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <span className="text-teal-600">
                {collapsedChapters[chapter] ? (
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </span>
              {chapter}
              <span className="text-sm sm:text-base font-normal text-gray-500 ml-2">
                ({chapterVideos.length} {chapterVideos.length === 1 ? 'video' : 'videos'})
              </span>
            </h2>
          </div>
          {!collapsedChapters[chapter] && (
          <div className="bg-white rounded-b-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              {/* Mobile Card View */}
              <div className="block md:hidden">
                {chapterVideos.map(video => (
                  <div key={video.id} className="border-b border-gray-200 p-4">
                    {editingId === video.id ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          name="title"
                          value={editData.title}
                          onChange={handleEditChange}
                          className="p-2 border rounded w-full text-gray-900 text-sm"
                          placeholder="Title"
                        />
                        <input
                          type="url"
                          name="url"
                          value={editData.url}
                          onChange={handleEditChange}
                          className="p-2 border rounded w-full text-gray-900 text-sm"
                          placeholder="URL"
                        />
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            name="published"
                            checked={editData.published}
                            onChange={handleEditChange}
                            className="h-4 w-4 text-teal-600"
                          />
                          <label className="text-sm text-gray-700">Published</label>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); saveEdit(); }}
                            className="bg-green-600 text-white px-3 py-1.5 rounded text-sm flex-1"
                          >
                            Save
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditingId(null); }}
                            className="bg-gray-500 text-white px-3 py-1.5 rounded text-sm flex-1"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="mb-3">
                          <h3 className="font-medium text-gray-900 mb-1 break-words text-sm sm:text-base">{video.title}</h3>
                          <a
                            href={video.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-teal-600 hover:underline text-xs sm:text-sm break-all"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {video.url}
                          </a>
                        </div>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={video.published !== false}
                              onChange={(e) => {
                                e.stopPropagation();
                                togglePublishStatus(video.id, video.published !== false);
                              }}
                              className="h-4 w-4 text-teal-600"
                            />
                            <span className="text-xs text-gray-700">
                              {video.published !== false ? "Published" : "Draft"}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); startEditing(video); }}
                            className="bg-teal-600 text-white px-3 py-1.5 rounded text-xs"
                          >
                            Edit
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(video.id); }}
                            className="bg-red-600 text-white px-3 py-1.5 rounded text-xs"
                          >
                            Delete
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleVideoPreview(video.id); }}
                            className="bg-purple-600 text-white px-3 py-1.5 rounded text-xs"
                          >
                            {expandedVideoId === video.id ? "Hide" : "Preview"}
                          </button>
                        </div>
                        {expandedVideoId === video.id && (
                          <div className="mt-3">
                            <div className="relative w-full pt-[56.25%] bg-black rounded-lg overflow-hidden">
                              <iframe
                                src={getEmbedUrl(video.url)}
                                className="absolute top-0 left-0 w-full h-full"
                                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                                allowFullScreen
                                frameBorder="0"
                                style={{ border: 'none' }}
                                title={video.title}
                                referrerPolicy="origin"
                                sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads allow-presentation"
                              />
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Desktop Table View */}
              <table className="hidden md:table min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-300 text-black">
                  <tr>
                    <th
                      className="px-4 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort("title")}
                    >
                      Title {sortConfig.key === "title" && (sortConfig.direction === "asc" ? "↑" : "↓")}
                    </th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      URL
                    </th>
                    <th
                      className="px-4 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort("published")}
                    >
                      Status {sortConfig.key === "published" && (sortConfig.direction === "asc" ? "↑" : "↓")}
                    </th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {chapterVideos.map(video => (
                    <React.Fragment key={video.id}>
                      <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleVideoPreview(video.id)}>
                        {editingId === video.id ? (
                          <>
                            <td className="px-4 lg:px-6 py-4">
                              <input
                                type="text"
                                name="title"
                                value={editData.title}
                                onChange={handleEditChange}
                                className="p-1 border rounded w-full text-gray-900 text-sm"
                              />
                            </td>
                            <td className="px-4 lg:px-6 py-4">
                              <input
                                type="url"
                                name="url"
                                value={editData.url}
                                onChange={handleEditChange}
                                className="p-1 border rounded w-full text-gray-900 text-sm"
                              />
                            </td>
                            <td className="px-4 lg:px-6 py-4">
                              <input
                                type="checkbox"
                                name="published"
                                checked={editData.published}
                                onChange={handleEditChange}
                                className="h-5 w-5 text-teal-600"
                              />
                            </td>
                            <td className="px-4 lg:px-6 py-4 space-x-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); saveEdit(); }}
                                className="bg-green-600 text-white px-3 py-1 rounded text-sm"
                              >
                                Save
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setEditingId(null); }}
                                className="bg-gray-500 text-white px-3 py-1 rounded text-sm"
                              >
                                Cancel
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 lg:px-6 py-4 font-medium text-gray-900 break-words">
                              {video.title}
                            </td>
                            <td className="px-4 lg:px-6 py-4 truncate max-w-xs text-gray-900">
                              <a
                                href={video.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-teal-600 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {video.url}
                              </a>
                            </td>
                            <td className="px-4 lg:px-6 py-4">
                              <input
                                type="checkbox"
                                checked={video.published !== false}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  togglePublishStatus(video.id, video.published !== false);
                                }}
                                className="h-5 w-5 text-teal-600"
                              />
                              <span className="ml-2 text-gray-700 text-sm">
                                {video.published !== false ? "Published" : "Draft"}
                              </span>
                            </td>
                            <td className="px-4 lg:px-6 py-4 space-x-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); startEditing(video); }}
                                className="bg-teal-600 text-white px-3 py-1 rounded text-sm"
                              >
                                Edit
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDelete(video.id); }}
                                className="bg-red-600 text-white px-3 py-1 rounded text-sm"
                              >
                                Delete
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleVideoPreview(video.id); }}
                                className="bg-purple-600 text-white px-3 py-1 rounded text-sm"
                              >
                                {expandedVideoId === video.id ? "Hide" : "Preview"}
                              </button>
                            </td>
                          </>
                        )}
                      </tr>
                      {expandedVideoId === video.id && (
                        <tr>
                          <td colSpan="4" className="px-4 lg:px-6 py-4 bg-gray-50">
                            <div className="relative w-full pt-[56.25%] bg-black rounded-lg overflow-hidden">
                              <iframe
                                src={getEmbedUrl(video.url)}
                                className="absolute top-0 left-0 w-full h-full"
                                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                                allowFullScreen
                                frameBorder="0"
                                style={{ border: 'none' }}
                                title={video.title}
                                referrerPolicy="origin"
                                sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads allow-presentation"
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          )}
        </div>
      ))}

      {filteredVideos.length === 0 && (
        <div className="p-4 text-center text-gray-500">
          No videos found matching your criteria
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkImport && (
        <DriveBulkImport
          activeCourse={activeCourse}
          onClose={() => setShowBulkImport(false)}
          onImportComplete={(count) => {
            alert(`Successfully imported ${count} videos!`);
            setShowBulkImport(false);
          }}
        />
      )}
    </div>
  );
};

export default AdminVideos;